import {createSelector} from 'reselect';

import {
  BlockActions,
  BlockActionTypes,
  CreatedBlockAction,
  CreatedBlocksAction,
  DeletedBlockAction,
  LoadedPreviewAction,
  UpdatedBlockAction,
} from '../actions/blockActions';
import {ApiStatus, IBlock,} from '../../models';
import {IState} from './index';
import {deleteObjProperty} from '../../utils/reducers';

interface BlockPreview {
  [key: string]: any; // Object of type {uid: req. response}
}

export const initialBlockState: BlockState = {
  creatingStatus: null,
  updatingStatus: null,
  deletingStatus: null,
  previewStatus: null,
  blocks: null,
  previews: null,
}

export interface BlockState {
  creatingStatus: ApiStatus | null,
  updatingStatus: ApiStatus | null;
  deletingStatus: ApiStatus | null;
  previewStatus: ApiStatus | null;
  blocks: IBlock[] | null;
  previews: BlockPreview | null;
}

export default function blockReducer(
  state: BlockState = initialBlockState,
  action: BlockActions
): BlockState {
  switch (action.type) {
    case BlockActionTypes.UNSET_BLOCKS:
      return {
        ...state,
        blocks: null,
      }
    case BlockActionTypes.LOADED_BLOCKS:
      return {
        ...state,
        blocks: action.payload?.map(toBlock) || [],
      }
    case BlockActionTypes.CREATE_BLOCK:
    case BlockActionTypes.CREATE_BLOCKS:
      return {
        ...state,
        creatingStatus: ApiStatus.LOADING,
      }
    case BlockActionTypes.CREATED_BLOCK:
      return handleCreateBlock(state, action);
    case BlockActionTypes.CREATED_BLOCKS:
      return handleCreateBlocks(state, action);
    case BlockActionTypes.CREATING_BLOCK_FAILED:
    case BlockActionTypes.CREATING_BLOCKS_FAILED:
      return {
        ...state,
        creatingStatus: ApiStatus.FAILED,
      }

    case BlockActionTypes.UPDATE_BLOCK:
    case BlockActionTypes.UPDATE_BLOCKS:
      return {
        ...state,
        updatingStatus: ApiStatus.LOADING,
      }
    case BlockActionTypes.UPDATING_BLOCK_FAILED:
      return {
        ...state,
        updatingStatus: ApiStatus.FAILED,
      }
    case BlockActionTypes.UPDATED_BLOCK:
      return handleUpdateBlock(state, action);
    case BlockActionTypes.UPDATED_BLOCKS:
      return handleUpdateBlocks(state, action);
    case BlockActionTypes.DELETE_BLOCK:
      return {
        ...state,
        deletingStatus: ApiStatus.LOADING,
      }
    case BlockActionTypes.DELETING_BLOCK_FAILED:
      return {
        ...state,
        deletingStatus: ApiStatus.FAILED,
      }
    case BlockActionTypes.DELETED_BLOCK:
      return handleDeleteBlock(state, action);

    case BlockActionTypes.LOAD_PREVIEW:
      return {
        ...state,
        previewStatus: ApiStatus.LOADING,
      }
    case BlockActionTypes.LOADED_PREVIEW:
      return handleLoadedPreview(state, action);
    case BlockActionTypes.LOADING_PREVIEW_FAILED:
      return {
        ...state,
        previewStatus: ApiStatus.FAILED,
      }
    case BlockActionTypes.UNSET_PREVIEW:
      return {
        ...state,
        previews: state.previews ? deleteObjProperty(state.previews, action.payload) : state.previews,
      }
    default:
      return state;
  }
}

// Functions
const toBlock = (block: IBlock) => ({
  ...block,
  content_json: JSON.parse(block.content_json || {}),
});

function handleCreateBlock(state: BlockState, action: CreatedBlockAction): BlockState {
  const blocks = state.blocks || [];

  return {
    ...state,
    blocks: [
      toBlock(action.payload),
      ...blocks,
    ],
    creatingStatus: ApiStatus.LOADED,
  }
}

function handleCreateBlocks(state: BlockState, action: CreatedBlocksAction): BlockState {
  const blocks = state.blocks || [];

  return {
    ...state,
    blocks: [
      ...action.payload.map(toBlock),
      ...blocks,
    ],
    creatingStatus: ApiStatus.LOADED,
  }
}

function handleUpdateBlock(state: BlockState, action: UpdatedBlockAction): BlockState {
  /*
   * if `fields` is set, only update specified fields
   */
  const fields = action.payload.fields;

  return {
    ...state,
    updatingStatus: ApiStatus.LOADED,
    blocks: state.blocks?.map(block => {
      if (block.uid === action.payload.block.uid) {
        let newProps: any = toBlock(action.payload.block);

        if (fields?.length) {
          const newBlock: any = toBlock(action.payload.block);
          newProps = fields.reduce((acc: any, field: string) => {
            if (newBlock.hasOwnProperty(field)) {
              acc[field] = newBlock[field];
            }
            return acc;
          }, {});
        }

        return {
            ...block,
            ...newProps,
            is_dirty: true, // mark block as dirty
          }
      }
      return block;
    }) || [toBlock(action.payload.block)],
  }
}

function handleUpdateBlocks(state: BlockState, action: any): BlockState {
  const blocks = state.blocks || [];
  const updatedBlocks = action.payload.blocks || [];

  return {
    ...state,
    updatingStatus: ApiStatus.LOADED,
    blocks: blocks.map(block => {
      const updatedBlock = updatedBlocks.find((b: IBlock) => b.uid === block.uid);

      if (updatedBlock) {
        return {
          ...block,
          is_dirty: false, // block is no longer dirty after update
        };
      }
      return block;
    }),
  }
}

function handleDeleteBlock(state: BlockState, action: DeletedBlockAction): BlockState {
  /*
   * Try to find the block that's about to be removed.
   */
  const block = state.blocks?.find((b: any) => b.uid === action.payload);
  /*
   * Remove block by `uid`
   */
  let newBlocks = state.blocks?.filter(block => block.uid !== action.payload) || [];

  if (block && block.content_json.hasOwnProperty('batchId')) {
    /*
     * If removed block is found, and it has `batchId` property, remove related blocks as well (IF / FOR statements)
     */
    newBlocks = state.blocks?.filter((b: any) => b.content_json.batchId !== block.content_json.batchId) || [];
  }

  return {
    ...state,
    blocks: newBlocks,
    deletingStatus: ApiStatus.LOADED,
  }
}

function handleLoadedPreview(state: BlockState, action: LoadedPreviewAction): BlockState {
  return {
    ...state,
    previewStatus: ApiStatus.LOADED,
    previews: {
      ...state.previews || {},
      [action.payload.uid]: action.payload.result.preview,
    }
  }
}
// Selectors

export const getBlockSelector = (uid: string) => createSelector(
  (state: IState) => state.block.blocks,
  (blocks: IBlock[] | null): IBlock | null => {
    return blocks?.find((block: IBlock) => (block.uid === uid)) || null;
  },
);
